import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { Section, SectionHeading } from './Section';
import Reveal from './Reveal';
import { azure, emerald, glass, line } from '../theme';

// Product names stay in Latin script in both languages.
const CAPABILITIES = [
  'Dynamics 365',
  'Power Platform',
  'Dataverse',
  'Power BI',
  'Microsoft Azure',
  'Azure DevOps',
  'React Native',
  'SharePoint Online',
  'ISO 27001',
  'Azure Data Factory',
];

export default function AboutUs() {
  const { t } = useTranslation();

  const commitments = [1, 2, 3].map((n) => ({
    n: `0${n}`,
    title: t(`about.commitmentP${n}T`),
    desc: t(`about.commitmentP${n}D`),
  }));

  return (
    <Section id="about">
      <SectionHeading
        eyebrow={t('aboutSection.eyebrow')}
        title={t('aboutSection.title')}
      />

      <Box
        sx={{
          display: 'grid',
          gap: { xs: 6, md: 8 },
          gridTemplateColumns: { xs: '1fr', md: '1.15fr 1fr' },
          alignItems: 'start',
        }}
      >
        {/* Story + capabilities */}
        <Reveal>
          <Typography variant="body1" color="text.secondary">
            {t('about.subtitle')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2.5 }}>
            {t('aboutSection.body2')}
          </Typography>

          <Typography variant="h6" component="h3" sx={{ mt: 4, mb: 1.75 }}>
            {t('aboutSection.chipsTitle')}
          </Typography>
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
            {CAPABILITIES.map((c) => (
              <Chip key={c} label={c} size="small" sx={{ px: 0.5 }} />
            ))}
          </Stack>
        </Reveal>

        {/* Commitments */}
        <Reveal delay={140}>
          <Box
            sx={{
              p: { xs: 3, md: 3.5 },
              borderRadius: 5,
              border: `1px solid ${line}`,
              bgcolor: glass,
            }}
          >
            <Typography variant="overline" sx={{ color: azure[300] }}>
              {t('aboutSection.commitTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2.5 }}>
              {t('aboutSection.commitLead')}
            </Typography>
            <Stack spacing={0} divider={<Box sx={{ height: '1px', bgcolor: line, my: 2.5 }} />}>
              {commitments.map((c) => (
                <Box key={c.n} sx={{ display: 'flex', gap: 2.25 }}>
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: 800,
                      fontSize: '1.05rem',
                      lineHeight: 1.6,
                      color: 'transparent',
                      backgroundImage: `linear-gradient(135deg, ${azure[300]}, ${emerald[300]})`,
                      WebkitBackgroundClip: 'text',
                      backgroundClip: 'text',
                      flex: 'none',
                    }}
                  >
                    {c.n}
                  </Typography>
                  <Box>
                    <Typography variant="h6" component="h4" sx={{ mb: 0.5 }}>
                      {c.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {c.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
        </Reveal>
      </Box>
    </Section>
  );
}
